import { Card, Checkbox, Flex, Group, MultiSelect, Stack, Switch, TextInput, Textarea } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { UseQueryOptions } from '@tanstack/react-query';
import React, { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { useInitiatives } from '../../../api/initiative-client/initiative-client.hooks';
import type { Initiative } from '../../../api/initiative-client/initiative-client.type';
import type { Component } from '../../../api/pages-client/pages-client.type';
import { Measure, MeasureUnits, TimeAllocationType } from '../../../api/work-periods-client/work-periods-client.type';
import { useTranslation } from 'react-i18next';
import { STANDARD_DATE_FORMATS } from '../../../helpers/date-time-helpers';
import { capitalizeFirstLetter } from '../../../helpers/string-helpers/string-helpers';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { secondaryBase, skyBase, skyDark } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Divider } from '../../../ui-library/divider/divider';
import { Icon } from '../../../ui-library/icon/icon';
import { Select } from '../../../ui-library/select/select';
import { Tooltip } from '../../../ui-library/tooltip/tooltip';
import { Text } from '../../../ui-library/typography/typography';
import {
  DATE_RANGES,
  getObjectTypes,
  getSprintDateRanges,
  TEXT_WIDGET_TYPES,
  WIDGET_SIZES,
  WIDGET_TYPES,
} from '../dashboards.type';
import { useWidgetAvailableMeasures } from '../hooks/dashboards.hooks';
import type { WidgetFormData } from '../hooks/dashboards.hooks.type';
import { DashboardModalStyled } from './dashboard-modal';
import { DashboardWidget } from './dashboard-widget';
import { SafeColorInput } from './safe-color-input';
import { getDefaultObjectColor } from './widget.helpers';

interface AddWidgetModalProps {
  opened: boolean;
  onClose: () => void;
  onSave: (widget: Component) => void;
  initialData?: Component;
  isEditing?: boolean;
}

export const AddWidgetModal: FC<AddWidgetModalProps> = ({
  opened,
  onClose,
  onSave,
  initialData,
  isEditing = false,
}) => {
  const i18n = useTranslation();
  const objectTypes = useMemo(() => getObjectTypes(i18n.t), [i18n.t]);
  const sprintDateRanges = useMemo(() => getSprintDateRanges(i18n.t), [i18n.t]);
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const isOriginBoardEnabled = useGlobalStore(
    (state) => state.organization?.use_origin_board_id_for_work_periods_filtering ?? false,
  );
  const portfolio = useGlobalStore((state) => state.portfolio);
  const portfolios = useGlobalStore((state) => state.portfolios).filter((p) => p.organization?.id === organizationId);
  const teams = useGlobalStore((state) => state.teams);
  const { query: initiativesQuery, initiatives = [] } = useInitiatives(portfolio?.id, {
    enabled: !!portfolio?.id,
  } as UseQueryOptions<Initiative[]>);
  const hasInitiatives = initiativesQuery.isSuccess && !!initiatives.length;

  const baseForm = {
    name: '',
    description: '',
    metric: '',
    dateRange: 'last_30_days',
    startDate: null,
    endDate: null,
    objectType: '',
    objects: [] as string[],
    widgetType: 'line',
    widgetSize: 'standard',
    showTitle: true,
    colorMap: {},
    measureColors: '',
    timeAllocation: null,
    includeActiveSprint: false,
  } as WidgetFormData;
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(baseForm);
  const [metrics, setMetrics] = useState<Array<{ value: string; label: string }>>([]);
  const [measureColorsInitialized, setMeasureColorsInitialized] = useState(false);
  const [previewMetric, setPreviewMetric] = useState<'tasks' | 'points'>('tasks');
  // Local-date YYYY-MM-DD (not UTC) so the picker's max-bound matches the user's wall-clock day.
  const today = new Date();
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // Handle available measures data first
  const { data: availableMeasuresData } = useWidgetAvailableMeasures(formData);

  // Check if this is a special sprint measure
  const isSpecialSprintMeasure =
    formData.objectType === 'sprint' &&
    (formData.metric === Measure.Burns ||
      formData.metric === Measure.FlowByPace ||
      formData.metric === Measure.FlowByPhase);

  // Auto-select chart type based on measure
  useEffect(() => {
    if (isSpecialSprintMeasure) {
      let chartType = 'line';
      switch (formData.metric) {
        case Measure.Burns:
          chartType = 'burn_down'; // burns -> burn down
          break;
        case Measure.FlowByPace:
          chartType = 'burn_up'; // flow_by_pace -> burn up
          break;
        case Measure.FlowByPhase:
          chartType = 'cumulative_flow'; // flow_by_phase -> cumulative flow
          break;
      }
      setFormData((prev) => ({ ...prev, widgetType: chartType }));
    }
  }, [formData.metric, isSpecialSprintMeasure]);

  // Compute objects based on current object type
  const availableObjects = useMemo(() => {
    let objects: Array<{ value: string; label: string }> = [];

    switch (formData.objectType) {
      case 'portfolio':
        objects = (portfolios || [])
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((p) => ({
            value: p.id,
            label: p.name,
          }));
        break;
      case 'project':
        objects = (teams || [])
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((team) => ({
            value: team.id,
            label: team.name,
          }));
        break;
      case 'board':
        objects = (teams || []).flatMap((team) =>
          team.subprojects
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((board) => ({
              value: board.id,
              label: `${team.name} - ${board.name}`,
            })),
        );
        break;
      case 'sprint':
        objects = (teams || []).flatMap((team) =>
          team.subprojects
            .filter((board) => board.sprint_count)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((board) => ({
              value: board.id,
              label: `${team.name} - ${board.name}`,
            })),
        );
        break;
      case 'initiative':
        if (hasInitiatives) {
          objects = (initiatives || [])
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((initiative: Initiative) => ({
              value: initiative.id,
              label: initiative.name,
            }));
        }
        break;
    }

    // Deduplicate by value (id) to prevent Mantine crashes from duplicate keys
    return Array.from(new Map(objects.map((obj) => [obj.value, obj])).values());
  }, [formData.objectType, portfolios, teams, initiatives, hasInitiatives]);

  // Update metrics when available measures data changes
  useEffect(() => {
    if (!availableMeasuresData) {
      return;
    }

    // Update metrics if we have one or more objects selected
    if (formData.objects.length === 0) {
      if (metrics.length > 0) {
        setMetrics([]);
      }
      return;
    }

    const newMetricsData = availableMeasuresData.map((measure) => {
      let label = measure.measure_title as string;

      // Override labels for special sprint chart measures
      if (formData.objectType === 'sprint') {
        if (measure.measure_name === Measure.Burns) {
          label = 'Burn Down';
        } else if (measure.measure_name === Measure.FlowByPace) {
          label = 'Burn Up';
        } else if (measure.measure_name === Measure.FlowByPhase) {
          label = 'Cumulative Flow';
        }
      }

      return {
        value: measure.measure_name as string,
        label,
      };
    });

    // Only update state if the metrics have actually changed
    const metricsChanged =
      metrics.length !== newMetricsData.length ||
      newMetricsData.some(
        (newMetric, index) => newMetric.value !== metrics[index]?.value || newMetric.label !== metrics[index]?.label,
      );

    if (metricsChanged) {
      setMetrics(newMetricsData);
    }
  }, [availableMeasuresData, formData.objects.length, formData.objectType, metrics]);

  // Auto-set widget type based on measure units
  useEffect(() => {
    if (!availableMeasuresData || !formData.metric) {
      return;
    }

    const selectedMeasure = availableMeasuresData.find((measure) => measure.measure_name === formData.metric);
    if (selectedMeasure) {
      if (selectedMeasure.measure_units === MeasureUnits.Text && formData.widgetType !== 'text') {
        setFormData((prev) => ({ ...prev, widgetType: 'text' }));
      } else if (selectedMeasure.measure_units !== MeasureUnits.Text && formData.widgetType === 'text') {
        setFormData((prev) => ({ ...prev, widgetType: 'line' }));
      }
    }
  }, [availableMeasuresData, formData.metric, formData.widgetType]);

  // Auto-switch to line chart when multiple measures are selected
  useEffect(() => {
    if (formData.metric) {
      const measures = formData.metric.split(',').filter((m) => m.trim());
      if (measures.length > 1 && !['line', 'area', 'bar'].includes(formData.widgetType)) {
        setFormData((prev) => ({ ...prev, widgetType: 'line' }));
      }
    }
  }, [formData.metric, formData.widgetType]);

  // Initialize measure colors when entering step 3 with multiple measures
  useEffect(() => {
    if (step === 3) {
      const measures = formData.metric ? formData.metric.split(',').filter((m) => m.trim()) : [];
      const existingColors = formData.measureColors
        ? formData.measureColors.split(',').map((color) => color.trim())
        : [];
      const hasExistingColors = measures.every((_, index) => Boolean(existingColors[index]));

      if (measures.length > 1 && !measureColorsInitialized && !hasExistingColors) {
        // Fill only the slots missing a color (e.g. a measure added after the widget was
        // customized) — never regenerate colors for measures that already have one. Indexing
        // into existingColors (not a filtered array) keeps each measure aligned to its own slot.
        const mergedColors = measures.map(
          (measure, index) => existingColors[index] || getDefaultObjectColor(measure, {}, index),
        );
        setFormData((prev) => ({ ...prev, measureColors: mergedColors.join(',') }));
      }
      if (measures.length > 1) {
        setMeasureColorsInitialized(true);
      }
    } else if (step < 3) {
      // Reset initialization flag when going back to earlier steps
      setMeasureColorsInitialized(false);
    }
  }, [step, formData.metric, formData.measureColors, measureColorsInitialized]);

  // Initialize form data when editing
  useEffect(() => {
    if (initialData && isEditing) {
      const componentData = initialData.component_data?.[0];
      if (componentData) {
        setFormData({
          name: initialData.name,
          description: initialData.description || '',
          metric: componentData.measure,
          dateRange: componentData.range_type,
          startDate: componentData.start_date ?? null,
          endDate: componentData.end_date ?? null,
          objectType: componentData.object_type,
          objects: componentData.object_ids,
          widgetType: initialData.type === 'text' ? 'text' : initialData.sub_type || 'line',
          widgetSize: 'standard',
          showTitle: initialData.show_title,
          colorMap: componentData.color_map || {},
          measureColors: componentData.measure_colors || '',
          timeAllocation: componentData.time_allocation_type,
          includeActiveSprint: componentData.include_current_sprint ?? false,
        });
        // Custom colors already loaded from the saved widget — don't let the step-3 effect
        // clobber them with freshly-generated defaults. Only skip that effect entirely when
        // every measure already has a saved color; a partial list still needs backfilling.
        const savedMeasures = componentData.measure ? componentData.measure.split(',').filter((m) => m.trim()) : [];
        const savedColors = componentData.measure_colors
          ? componentData.measure_colors.split(',').map((color) => color.trim())
          : [];
        setMeasureColorsInitialized(savedMeasures.every((_, index) => Boolean(savedColors[index])));
      }
    }
  }, [initialData, isEditing]);

  /**
   * Builds the in-memory `Component` shape used for both the live preview and
   * the payload handed to `onSave`. Mirrors `formData` into the persisted
   * widget format and applies the custom-range inclusion rule: `start_date`
   * and `end_date` are emitted only when `dateRange === 'custom'` AND the
   * matching `formData.startDate`/`formData.endDate` are populated, so
   * predefined ranges never leak stale custom dates into the payload.
   *
   * Also resolves the widget's `type`/`sub_type` from `formData.widgetType`:
   * `table`/`tile`/`text` collapse to `type` with no `sub_type`; everything
   * else is treated as a chart sub-type. `id`/`page` fall back to the
   * `initialData` (edit mode) or empty/`'preview'` (create mode).
   *
   * @returns {Component} A Component ready to render in the preview pane or to
   *   pass to the parent's `onSave` handler.
   */
  const getPreviewComponent = (): Component => {
    const isCustomRange = formData.dateRange === 'custom';
    return {
      id: initialData?.id || '',
      page: initialData?.page || '',
      name: formData.name,
      description: formData.description,
      type: ['table', 'tile', 'text'].includes(formData.widgetType) ? formData.widgetType : 'chart',
      sub_type: ['table', 'tile', 'text'].includes(formData.widgetType) ? undefined : formData.widgetType,
      show_title: formData.showTitle,
      component_data: [
        {
          id: initialData?.component_data?.[0]?.id || 'preview',
          measure: formData.metric,
          range_type: formData.dateRange,
          object_type: formData.objectType,
          object_ids: formData.objects,
          color_map: formData.colorMap,
          measure_colors: formData.measureColors,
          time_allocation_type: formData.timeAllocation as TimeAllocationType,
          include_current_sprint: formData.objectType === 'sprint' ? formData.includeActiveSprint : undefined,
          start_date: isCustomRange && formData.startDate ? formData.startDate : undefined,
          end_date: isCustomRange && formData.endDate ? formData.endDate : undefined,
        },
      ],
    };
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1) {
      setStep(2);
      return;
    }
    if (step === 2) {
      // For special sprint measures, skip step 3 (color customization)
      if (isSpecialSprintMeasure) {
        setFormData(baseForm);
        setStep(1);
        onSave(getPreviewComponent());
        return;
      }
      // Otherwise, proceed to step 3
      setStep(3);
      return;
    }
    setFormData(baseForm);
    setStep(1);
    onSave(getPreviewComponent());
  };

  const getWidgetPreview = () => (
    <Card withBorder style={{ height: '500px' }}>
      <Text size="regular" weight="bold" style={{ marginBottom: '1rem' }}>
        Preview
      </Text>
      <DashboardWidget
        component={getPreviewComponent()}
        onEdit={() => {}}
        onDelete={() => {}}
        isPreview={true}
        previewMetric={previewMetric}
        onPreviewMetricChange={setPreviewMetric}
      />
    </Card>
  );

  const renderStep1 = () => (
    <Fragment>
      <Text size="regular" color="dimmed" style={{ marginBottom: '1rem' }}>
        Easily create a new widget to tailor your dashboard to your needs.
      </Text>
      <Flex direction="column" gap="md">
        <TextInput
          label="Widget Name"
          placeholder="QA Test Project"
          required
          value={formData.name}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFormData({ ...formData, name: event.target.value })
          }
        />

        <Textarea
          label="Description"
          placeholder="Measures the average time taken by the QA team to respond to assigned tasks or issues."
          value={formData.description}
          onChange={(event) => setFormData({ ...formData, description: event.target.value })}
          minRows={2}
        />

        <Group grow>
          <div>
            <Text size="regular" weight="bold" style={{ marginBottom: '0.25rem' }}>
              Select Object Type{' '}
            </Text>
            <Select
              options={objectTypes}
              value={formData.objectType}
              onChange={(value) => {
                setFormData({
                  ...formData,
                  objectType: value,
                  objects: [],
                  metric: '',
                  dateRange: '',
                  startDate: null,
                  endDate: null,
                  includeActiveSprint: false,
                });
              }}
              placeholder="Select object type"
              searchable
            />
          </div>
          <div>
            <Flex align="center" gap={4} style={{ marginBottom: '0.25rem' }}>
              <Text size="regular" weight="bold">
                Select Objects{' '}
              </Text>
              {formData.objectType === 'sprint' && isOriginBoardEnabled && (
                <Tooltip label="If your board is missing, it likely has no sprints" variant="black" zIndex={1000}>
                  <span tabIndex={0} style={{ display: 'inline-flex', cursor: 'pointer' }}>
                    <Icon name="info" size={16} color={skyDark} />
                  </span>
                </Tooltip>
              )}
            </Flex>
            {formData.objectType === 'sprint' ? (
              <Select
                value={formData.objects.length > 0 ? formData.objects[0] : null}
                options={availableObjects}
                onChange={(value) => setFormData({ ...formData, objects: value ? [value] : [] })}
                placeholder="Select a sprint"
                searchable
              />
            ) : (
              <MultiSelect
                value={formData.objects}
                data={availableObjects}
                styles={{
                  input: {
                    borderRadius: '16px',
                  },
                  dropdown: {
                    zIndex: 1000,
                    borderRadius: '16px',
                    fontSize: '16px',
                    fontWeight: '800',
                  },
                }}
                onChange={(value) => setFormData({ ...formData, objects: value })}
                placeholder="Select objects"
                searchable
                disabled={!formData.objectType}
              />
            )}
          </div>
        </Group>

        {formData.objectType && formData.objects.length > 0 && (
          <Group grow>
            <div>
              <Text size="regular" weight="bold" style={{ marginBottom: '0.25rem' }}>
                Select Metric{isSpecialSprintMeasure ? '' : '(s)'}{' '}
              </Text>
              {isSpecialSprintMeasure ? (
                <Select
                  options={metrics}
                  value={formData.metric}
                  onChange={(value) => setFormData({ ...formData, metric: value || '' })}
                  placeholder="Select a metric"
                  searchable
                />
              ) : (
                <MultiSelect
                  data={metrics}
                  value={formData.metric ? formData.metric.split(',').filter((m) => m) : []}
                  onChange={(value) => {
                    // Special sprint measures (Burns, FlowByPace, FlowByPhase) are mutually exclusive with other measures
                    const specialMeasures = [Measure.Burns, Measure.FlowByPace, Measure.FlowByPhase];
                    const lastSelectedMeasure = value[value.length - 1];

                    // If the last selected measure is a special measure, only keep that one
                    if (lastSelectedMeasure && specialMeasures.includes(lastSelectedMeasure as Measure)) {
                      setFormData({ ...formData, metric: lastSelectedMeasure });
                    }
                    // If any existing measure is special and user tries to add another, replace with the new one
                    else if (value.some((m) => specialMeasures.includes(m as Measure))) {
                      // Remove all special measures if user is adding a regular measure
                      const filteredValue = value.filter((m) => !specialMeasures.includes(m as Measure));
                      if (filteredValue.length <= 4) {
                        setFormData({ ...formData, metric: filteredValue.join(',') });
                      }
                    }
                    // Regular measures - limit to 4
                    else if (value.length <= 4) {
                      setFormData({ ...formData, metric: value.join(',') });
                    }
                  }}
                  placeholder="Select metric(s) (max 4)"
                  searchable
                  styles={{
                    input: {
                      borderRadius: '16px',
                    },
                    dropdown: {
                      zIndex: 1000,
                      borderRadius: '16px',
                      fontSize: '16px',
                      fontWeight: '800',
                    },
                  }}
                />
              )}
            </div>
            {formData.objectType === 'sprint' ? (
              <div>
                <Group justify="space-between" align="center" wrap="nowrap" style={{ marginBottom: '0.25rem' }}>
                  <Text size="regular" weight="bold">
                    {`Last N ${i18n.t('common.sprints')}`}
                  </Text>
                  <Switch
                    label="Include Active Sprint"
                    color={secondaryBase}
                    checked={formData.includeActiveSprint}
                    onChange={(event) => setFormData({ ...formData, includeActiveSprint: event.currentTarget.checked })}
                    styles={{ track: { cursor: 'pointer' }, label: { cursor: 'pointer' } }}
                  />
                </Group>
                <Select
                  options={sprintDateRanges}
                  value={formData.dateRange}
                  onChange={(value) => setFormData({ ...formData, dateRange: value })}
                  placeholder="Select a range"
                  searchable
                />
              </div>
            ) : (
              <div>
                <Text size="regular" weight="bold" style={{ marginBottom: '0.25rem' }}>
                  Select Date Range{' '}
                </Text>
                <Select
                  options={DATE_RANGES}
                  value={formData.dateRange}
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      dateRange: value,
                      startDate: value === 'custom' ? formData.startDate : null,
                      endDate: value === 'custom' ? formData.endDate : null,
                    })
                  }
                  placeholder="Last 30 Days"
                  searchable
                />
              </div>
            )}
            {formData.objectType !== 'sprint' && (
              <div>
                <Text size="regular" weight="bold" style={{ marginBottom: '0.25rem' }}>
                  Select Time Allocation{' '}
                </Text>
                <Select
                  options={Object.values(TimeAllocationType).map((value) => ({
                    value,
                    label: capitalizeFirstLetter(value),
                  }))}
                  value={formData.timeAllocation as string}
                  onChange={(value: string | null) => setFormData({ ...formData, timeAllocation: value })}
                  placeholder="Daily"
                  searchable
                />
              </div>
            )}
          </Group>
        )}
        {formData.objectType !== 'sprint' && formData.dateRange === 'custom' && (
          <Group grow>
            <DatePickerInput
              label="Start Date"
              required
              radius="xl"
              placeholder="Select start date"
              leftSection={<Icon name="calendar_month" color={secondaryBase} size={16} />}
              leftSectionPointerEvents="none"
              value={formData.startDate ?? undefined}
              maxDate={formData.endDate ?? todayIso}
              onChange={(value) => setFormData({ ...formData, startDate: value ?? null })}
              pointer
              popoverProps={{ withinPortal: true, zIndex: 1001, position: 'bottom-start' }}
              valueFormat={STANDARD_DATE_FORMATS.FULL_DATE}
            />
            <DatePickerInput
              label="End Date"
              required
              radius="xl"
              placeholder="Select end date"
              leftSection={<Icon name="calendar_month" color={secondaryBase} size={16} />}
              leftSectionPointerEvents="none"
              value={formData.endDate ?? undefined}
              minDate={formData.startDate ?? undefined}
              maxDate={todayIso}
              onChange={(value) => setFormData({ ...formData, endDate: value ?? null })}
              pointer
              popoverProps={{ withinPortal: true, zIndex: 1001, position: 'bottom-start' }}
              valueFormat={STANDARD_DATE_FORMATS.FULL_DATE}
            />
          </Group>
        )}
      </Flex>
    </Fragment>
  );

  const renderStep2 = () => (
    <Fragment>
      <Flex direction="column" gap="md">
        <Group grow>
          <div>
            <Text size="regular" weight="bold" style={{ marginBottom: '0.25rem' }}>
              Widget Type{' '}
            </Text>
            <Select
              value={formData.widgetType}
              options={(() => {
                if (
                  availableMeasuresData?.find((measure) => measure.measure_name === formData.metric)?.measure_units ===
                  MeasureUnits.Text
                ) {
                  return TEXT_WIDGET_TYPES;
                }

                // Special sprint measures: only show their specific chart types
                if (isSpecialSprintMeasure) {
                  if (formData.metric === Measure.Burns) {
                    return WIDGET_TYPES.filter((type) => type.value === 'burn_down');
                  } else if (formData.metric === Measure.FlowByPace) {
                    return WIDGET_TYPES.filter((type) => type.value === 'burn_up');
                  } else if (formData.metric === Measure.FlowByPhase) {
                    return WIDGET_TYPES.filter((type) => type.value === 'cumulative_flow');
                  }
                }

                if (formData.metric && formData.metric.split(',').filter((m) => m.trim()).length > 1) {
                  return WIDGET_TYPES.filter((type) => ['line', 'area', 'bar'].includes(type.value));
                }

                // Default: filter out special chart types (burn_up, burn_down, cumulative_flow)
                return WIDGET_TYPES.filter((type) => !['burn_up', 'burn_down', 'cumulative_flow'].includes(type.value));
              })()}
              onChange={(value) => setFormData({ ...formData, widgetType: value })}
              placeholder="Select widget type"
              searchable
            />
          </div>
          <div>
            <Text size="regular" weight="bold" style={{ marginBottom: '0.25rem' }}>
              Widget Size{' '}
            </Text>
            <Select
              value={formData.widgetSize}
              options={WIDGET_SIZES}
              onChange={(value) => setFormData({ ...formData, widgetSize: value })}
              placeholder="Select widget size"
              searchable
            />
          </div>
        </Group>
        <Group>
          <Checkbox
            label="Show Title"
            checked={formData.showTitle}
            color={secondaryBase}
            onChange={(event) => setFormData({ ...formData, showTitle: event.currentTarget.checked })}
          />
        </Group>
        {getWidgetPreview()}
      </Flex>
    </Fragment>
  );
  const renderStep3 = () => {
    const selectedMeasures = formData.metric ? formData.metric.split(',').filter((m) => m.trim()) : [];
    const hasMultipleMeasures = selectedMeasures.length > 1;

    // Parse measure colors and ensure we have a color for each measure
    const getMeasureColors = () => {
      const colors = formData.measureColors
        ? formData.measureColors
            .split(',')
            .map((c) => c.trim())
            .filter((c) => c)
        : [];

      // Ensure we have enough colors, fill missing with defaults
      while (colors.length < selectedMeasures.length) {
        colors.push(getDefaultObjectColor(selectedMeasures[colors.length], {}, colors.length));
      }

      return colors;
    };

    const measureColorsArray = getMeasureColors();

    return (
      <Fragment>
        <Text size="medium" weight="bold">
          Color Customization
        </Text>
        {!isSpecialSprintMeasure && (
          <Stack style={{ marginBottom: '1rem' }}>
            {hasMultipleMeasures
              ? // Color pickers for each measure
                selectedMeasures.map((measure, index) => {
                  const measureLabel = metrics.find((m) => m.value === measure)?.label || measure;
                  return (
                    <Group justify="space-between" key={measure}>
                      <Text size="regular" weight="medium" style={{ marginBottom: '0.25rem' }}>
                        {measureLabel}
                      </Text>
                      <SafeColorInput
                        value={measureColorsArray[index] || '#000000'}
                        onChange={(value) => {
                          if (!value || value.trim() === '') {
                            return;
                          }
                          const newColors = [...measureColorsArray];
                          newColors[index] = value;
                          setFormData((prev) => ({
                            ...prev,
                            measureColors: newColors.join(','),
                          }));
                        }}
                        label={measureLabel}
                      />
                    </Group>
                  );
                })
              : // Color pickers for each object
                availableObjects
                  .filter((object) => formData.objects.includes(object.value))
                  .map((object) => (
                    <Group justify="space-between" key={object.value}>
                      <Text size="regular" weight="medium" style={{ marginBottom: '0.25rem' }}>
                        {object.label}
                      </Text>
                      <SafeColorInput
                        value={
                          formData.colorMap[object.value] ||
                          getDefaultObjectColor(object.value, formData.colorMap, formData.objects.indexOf(object.value))
                        }
                        onChange={(value) =>
                          setFormData((prev) => ({
                            ...prev,
                            colorMap: { ...prev.colorMap, [object.value]: value },
                          }))
                        }
                        label={object.label}
                      />
                    </Group>
                  ))}
          </Stack>
        )}
        {getWidgetPreview()}
      </Fragment>
    );
  };

  const isStep1Valid =
    formData.metric !== '' &&
    formData.dateRange !== '' &&
    formData.objects.length > 0 &&
    formData.objectType !== '' &&
    (formData.dateRange !== 'custom' || (!!formData.startDate && !!formData.endDate));

  return (
    <DashboardModalStyled
      opened={opened}
      onClose={onClose}
      title={
        <div style={{ display: 'flex', gap: '8px' }}>
          <Text size="large" weight="bold">
            {isEditing ? 'Edit Widget' : 'Add Widget'}
          </Text>
          <Text size="large" weight="medium" color={skyBase}>
            (Step {step} of {isSpecialSprintMeasure ? 2 : 3})
          </Text>
        </div>
      }
      size="xl"
      padding="lg"
      centered
    >
      <form
        onSubmit={(e) => {
          handleSubmit(e);
        }}
      >
        {step === 1 ? renderStep1() : step === 2 ? renderStep2() : !isSpecialSprintMeasure ? renderStep3() : null}
        <Divider style={{ marginTop: '32px' }} />
        <Flex justify="space-between" mt="xl">
          <div>
            {step !== 1 && (
              <Button
                variant="outline"
                radius="xl"
                onClick={() => setStep(step - 1)}
                leftSection={<Icon name="arrow_back" size={16} color={skyBase} />}
              >
                Back
              </Button>
            )}
          </div>
          <Flex gap="md">
            <Button variant="outline" radius="xl" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={step === 1 && !isStep1Valid}
              radius="xl"
              variant="secondary"
              rightSection={
                <Icon
                  name={(step === 2 && isSpecialSprintMeasure) || step === 3 ? 'check_circle' : 'arrow_forward'}
                  size={16}
                  color="white"
                />
              }
            >
              {(step === 2 && isSpecialSprintMeasure) || step === 3
                ? isEditing
                  ? 'Save Changes'
                  : `Create ${i18n.t('common.dashboard.widget')}`
                : 'Next Step'}
            </Button>
          </Flex>
        </Flex>
      </form>
    </DashboardModalStyled>
  );
};
